import { FC, useRef } from "react";

import { useForm } from "react-hook-form";

import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../common/LiveFormEditorContext";
import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { LiveFormEditorV2StepLogoStateless, StepLogoFormData } from "./LiveFormEditorV2StepLogo.stateless";

import { trpc } from "@/common/helpers/trpc";
import { ImageUploaderHandle } from "@/components/ImageUploader/types";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";

export const DonationLiveEditorStepLogo: FC = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiDonation, optionalFundraiserToken } = useLiveFormEditorContext<"donation-form">();
  const { liveFormAmplitudePayload } = useLiveFormAmplitudeEvents();

  const logoRef = useRef<ImageUploaderHandle>(null);

  const defaultValues: StepLogoFormData = {
    logo: apiDonation.logoUrl,
    isUploading: false,
  };
  const form = useForm<StepLogoFormData>({ defaultValues });

  const { mutateAsync: updateLogo } = trpc.updateDonationFormLogo.useMutation();
  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepLogoFormData>({
    type: "donation-form",
    form,
    saveData: async (formData) => {
      const finalLogoUrl = (await logoRef.current?.finalizeSquareCrop()) ?? formData.logo;

      return updateLogo({
        donationFormId: apiDonation.id,
        logoUrl: finalLogoUrl,
        fundraiserToken: optionalFundraiserToken,
      });
    },
    errorMessage: t("dashboard", "liveFormEditor.stepLogo.error"),
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.donation.logoUrl = formData.logo;

      if (!formData.logo) {
        liveDataToMutate.placeholders.isLogo = true;
      }
    },
  });

  return (
    <LiveFormEditorV2StepLogoStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      handleSaveAndBack={handleSaveAndBack}
      amplitudePayload={liveFormAmplitudePayload}
      logoRef={logoRef}
      fundraiserContext={{ donationFormId: apiDonation.id, fundraiserToken: optionalFundraiserToken }}
    />
  );
};
